import React from "react";
import { Box, Divider, Typography, Grid } from "@mui/joy";
import { Assignment, PostalAddress } from "../../../api/assignments/Assignments.domain";

interface AssignmentReadOnlyFieldsProps {
  assignment: Assignment;
}

const AssignmentReadOnlyFields: React.FC<AssignmentReadOnlyFieldsProps> = ({ assignment }) => {
  const damagedObject = assignment.damagedManufacturedObject;

  return (
    <Box sx={{ width: "100%" }}>
      <Field label="Assignment ID" value={assignment.assignmentId} />
      <Field label="Benefit ID" value={assignment.benefitId} />
      <OptionalField label="Parent Benefit ID" value={assignment.parentBenefitId} />
      <OptionalField label="Claim ID" value={assignment.claimId} />
      <OptionalField label="External Assignment ID" value={assignment.externalAssignmentId} />
      <OptionalField
        label="Accepted Service Point Code"
        value={assignment.acceptedServicePointCode}
      />
      <OptionalField label="Status" value={assignment.assignmentStatus} />
      <OptionalField label="Last Update Date" value={assignment.lastUpdateDate} />
      <Divider />

      {assignment.eventType && (
        <Box>
          <Field label="Event Type" value={assignment.eventType.code} />
          <OptionalField label="Event Type Label" value={assignment.eventType.label} />
        </Box>
      )}
      <Divider />

      {assignment.useCase && (
        <Box>
          <Field label="Use Case Code" value={assignment.useCase.code} />
          {assignment.useCaseReasons &&
            assignment.useCaseReasons.map((r, idx) => (
              <Box key={idx} ml={2}>
                <Field label="Reason Code" value={r.code} />
                <OptionalField label="Reason Detail" value={r.freeField} />
              </Box>
            ))}
        </Box>
      )}
      <Divider />

      {assignment.servicePoint && (
        <Box>
          <Field label="SP Code" value={assignment.servicePoint?.servicePointCode} />
          <Field label="SP Name" value={assignment.servicePoint?.name} />
          {assignment.servicePoint?.type && (
            <Field label="SP Type Code" value={assignment.servicePoint?.type?.code} />
          )}
          {assignment.servicePoint?.logisticsMode && (
            <Field label="SP Logistics Mode" value={assignment.servicePoint?.logisticsMode?.code} />
          )}
          {assignment.servicePoint?.gpsCoordinates && (
            <Box ml={2}>
              <Field label="Latitude" value={assignment.servicePoint?.gpsCoordinates?.latitude} />
              <Field label="Longitude" value={assignment.servicePoint?.gpsCoordinates?.longitude} />
            </Box>
          )}
          {assignment.servicePoint?.postalAddress && (
            <Box ml={2}>
              <RenderPostalAddress
                address={assignment.servicePoint.postalAddress}
                indentLevel={2}
              />
            </Box>
          )}
          {assignment.servicePoint?.servicePointPostalAddress && (
            <Box ml={2}>
              <RenderPostalAddress
                address={assignment.servicePoint.servicePointPostalAddress}
                indentLevel={2}
              />
            </Box>
          )}
        </Box>
      )}
      <Divider />

      {damagedObject && (
        <Box>
          {damagedObject.manufacturedObjectSpecification && (
            <Box ml={2}>
              <Field
                label="Product Family"
                value={damagedObject.manufacturedObjectSpecification?.productFamily?.code}
              />
              <Field
                label="Product Type"
                value={damagedObject.manufacturedObjectSpecification?.type?.code}
              />
            </Box>
          )}

          <Field label="Brand Name" value={damagedObject.marketBrandName} />
          <Field label="Model Name" value={damagedObject.modelName} />
          <OptionalField label="Purchase Date" value={damagedObject.purchaseDate} />
          <OptionalField label="EAN Code" value={damagedObject.europeanArticleNumberingCode} />
          <OptionalField label="Serial Number" value={damagedObject.serialNumber} />

          {damagedObject.purchasePrice && (
            <Box ml={2}>
              <Field
                label="Purchase Amount"
                value={damagedObject.purchasePrice.amount || damagedObject.purchasePrice.value}
              />
              <Field label="Purchase Currency" value={damagedObject.purchasePrice.currency} />
            </Box>
          )}

          {damagedObject.replacementValue && (
            <Box ml={2}>
              <Field
                label="Replacement Amount"
                value={
                  damagedObject.replacementValue.amount || damagedObject.replacementValue.value
                }
              />
              <Field label="Replacement Currency" value={damagedObject.replacementValue.currency} />
            </Box>
          )}

          {damagedObject.characteristicsOfManufacturedObject && (
            <Box ml={2} mt={1}>
              <Typography level="body-sm" fontWeight="bold">
                Characteristics:
              </Typography>
              {damagedObject.characteristicsOfManufacturedObject.map((char, idx) => (
                <Box key={idx} ml={2} mt={0.5}>
                  <Field label="Type" value={char.characteristicsType} />
                  {char.basedOnObjectSpecification && (
                    <Field label="Specification" value={char.basedOnObjectSpecification.code} />
                  )}
                </Box>
              ))}
            </Box>
          )}

          {damagedObject.additionalCharacteristics &&
            damagedObject.additionalCharacteristics.length > 0 && (
              <Box ml={2} mt={1}>
                <Typography level="body-sm" fontWeight="bold">
                  Additional Characteristics:
                </Typography>
                {damagedObject.additionalCharacteristics.map((char, idx) => (
                  <>
                    <Box key={idx} ml={2} mt={0.5}>
                      <Field label="Type" value={char.additionalCharacteristicsType} />
                      {char.basedOnAdditionalSpecification && (
                        <Field
                          label={char.basedOnAdditionalSpecification.key}
                          value={char.basedOnAdditionalSpecification.value}
                        />
                      )}
                    </Box>
                    <Divider
                      sx={{
                        borderWidth: "1px",
                        borderColor: "divider",
                        width: "60%",
                        marginBottom: "20px",
                      }}
                    />
                  </>
                ))}
              </Box>
            )}
        </Box>
      )}
      <Divider />

      {assignment.committedCoverage && (
        <Box>
          <Typography level="body-sm" fontWeight="bold">
            Coverage:
          </Typography>
          {assignment.committedCoverage.coverageOption && (
            <Box ml={2}>
              <Field label="Option Code" value={assignment.committedCoverage.coverageOption.code} />
              <OptionalField
                label="Option Label"
                value={assignment.committedCoverage.coverageOption.label}
              />
            </Box>
          )}
          <OptionalField
            label="Option Category"
            value={assignment.committedCoverage.coverageOptionCategory}
            indent={1}
          />
          {assignment.committedCoverage.coverageSpecification && (
            <Box ml={2}>
              <Field
                label="Specification Code"
                value={assignment.committedCoverage.coverageSpecification.code}
              />
              <OptionalField
                label="Specification Label"
                value={assignment.committedCoverage.coverageSpecification.label}
              />
            </Box>
          )}
        </Box>
      )}
      <Divider />

      <Box>
        <Field label="Customer Type" value={assignment.customer.customerType} />
        {assignment.customer.customerType === "PersonRef" && (
          <Box ml={2}>
            <Field
              label="Full Name"
              value={`${(assignment.customer as any).personName?.prefixTitle?.code || ""} ${(assignment.customer as any).personName?.firstName || ""} ${(assignment.customer as any).personName?.lastName || ""}`.trim()}
            />
          </Box>
        )}
        {assignment.customer.customerType === "Organization" && (
          <Box ml={2}>
            <Field label="Organization Name" value={(assignment.customer as any).name} />
            <OptionalField label="Legal Status" value={(assignment.customer as any).legalStatus} />
          </Box>
        )}
        {assignment.customer.contactInformation && (
          <Box ml={2}>
            {assignment.customer.contactInformation.emails &&
              assignment.customer.contactInformation.emails.map((email, idx) => (
                <Field key={idx} label="Email" value={email.email} />
              ))}
            {assignment.customer.contactInformation.cellPhones &&
              assignment.customer.contactInformation.cellPhones.map((phone, idx) => (
                <Field key={idx} label="Cell Phone" value={phone.phone || phone.cellPhone} />
              ))}
          </Box>
        )}
      </Box>
      <Divider />

      <Box>
        <Typography level="body-sm" fontWeight="bold">
          Customer Use Address:
        </Typography>
        <RenderPostalAddress address={assignment.customerUseAddress} indentLevel={1} />
      </Box>
      <Divider />

      {assignment.pickUpAddress && (
        <>
          <Box>
            <Typography level="body-sm" fontWeight="bold">
              Pick Up Address:
            </Typography>
            <RenderPostalAddress address={assignment.pickUpAddress} indentLevel={1} />
          </Box>
          <Divider />
        </>
      )}

      {assignment.activities && assignment.activities.length > 0 && (
        <>
          <Box>
            <Typography level="body-sm" fontWeight="bold">
              Activities:
            </Typography>
          </Box>
          <Divider />
          <Box>
            {assignment.activities.map((activity, idx) => (
              <Box key={idx} ml={2} mb={1}>
                <Typography level="body-sm" fontWeight="bold" fontStyle="italic" mb={0.5}>
                  Activity #{idx + 1}:
                </Typography>
                <Field label="Issue Date" value={activity.issueDate} />
                <Field label="Activity Type" value={activity.activityType?.code} />
                <OptionalField label="Return Status" value={activity.returnStatus} />
                <OptionalField label="Failure Reason" value={activity.stepFailureReason?.label} />
                {activity.responseDetail?.freeField && (
                  <OptionalField label="Free Field" value={activity.responseDetail.freeField} />
                )}
                {activity.responseDetail?.dynamicProperties &&
                  activity.responseDetail.dynamicProperties.length > 0 && (
                    <Box ml={2}>
                      {activity.responseDetail.dynamicProperties.map((prop) => (
                        <Field key={prop.key} label={prop.key} value={prop.value} />
                      ))}
                    </Box>
                  )}
              </Box>
            ))}
          </Box>
          <Divider />
        </>
      )}
    </Box>
  );
};

export default AssignmentReadOnlyFields;

/**
 * Sub-component: Displays a label => value field,
 * always (but if value is null/undefined, displays "N/A").
 */
interface FieldProps {
  label: string;
  value: any;
  indent?: number;
}

function Field({ label, value, indent = 0 }: FieldProps) {
  const finalValue = value !== undefined && value !== null && value !== "" ? String(value) : "-";
  return (
    <Grid container spacing={1} sx={{ ml: indent * 2, mb: 0.5 }}>
      <Grid xs={4} sm={4} md={3}>
        <Typography level="body-sm" fontWeight="lg" textAlign="left">
          {label} :
        </Typography>
      </Grid>
      <Grid xs={8} sm={8} md={9}>
        <Typography level="body-md">{finalValue}</Typography>
      </Grid>
    </Grid>
  );
}

/**
 * Sub-component: Displays a label => value field only if
 * the value is not empty (null/undefined/"").
 */
function OptionalField({ label, value, indent }: FieldProps) {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return <Field label={label} value={value} indent={indent} />;
}

/**
 * Sub-component: Displays a complete PostalAddress.
 * Handles all fields, in "optional" mode if they are empty.
 */
interface RenderPostalAddressProps {
  address: PostalAddress;
  indentLevel?: number;
}

function RenderPostalAddress({ address }: RenderPostalAddressProps) {
  if (!address) return null;
  return (
    <Box>
      <OptionalField label="Recipient" value={address.recipientNameLine} />
      <OptionalField label="Complement" value={address.recipientComplementLine} />
      <OptionalField label="Building" value={address.buildingLine} />
      <Field
        label="Street"
        value={
          `${address.streetLine || address.street || ""}${address.streetNumber ? " " + address.streetNumber : ""}`.trim() ||
          null
        }
      />
      <OptionalField label="Locality" value={address.localityLine} />
      <OptionalField label="Postal Box" value={address.postalBoxLine} />
      <Field
        label="City/Zip Code"
        value={
          `${address.postalCode || ""} ${address.cityLine || address.city || ""}`.trim() || null
        }
      />
      <OptionalField label="State/Province" value={address.state} />
      <Field
        label="Country"
        value={
          address.countryLine?.label ||
          address.country?.label ||
          address.countryLine?.code ||
          address.country?.code ||
          null
        }
      />
    </Box>
  );
}
